import {
    type Content,
    type Extension,
    type FocusPosition,
    type Mark,
    type Node,
} from "@tiptap/react";
import type moment from "moment";
import { v4 as uuid } from "uuid";

import type { SerializableEditorModel } from "@btnet/services-btnet/handlers/models/serializableEditorModel";

import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { type EditorControlBreakpointNames } from "commonComponents/btWrappers/editor/core/EditorControlBar/EditorControlBar.types";
import { type IInputDebouncingContext } from "commonComponents/utilities/Input/InputDebouncingContext";

/**
 * Should match the current schema version on the backend
 */
export const currentSchemaVersion = "1.0.0";

export interface IEditorApiType {
    replyContent?: EditorContent;
    content: Content;
    schemaVersion: string;
    commentDate?: moment.Moment;
    userFullName?: string;
}

export class EditorContent {
    constructor(data: IEditorApiType, addUUid: boolean = true) {
        this.schemaVersion = data.schemaVersion;
        this.content = data.content;
        this.commentDate = data.commentDate;
        this.userFullName = data.userFullName;

        if (data.replyContent) {
            this.replyContent = new EditorContent(data.replyContent);
        }

        if (addUUid) {
            this._uuid = uuid();
        }
    }

    userFullName?: string;
    commentDate?: moment.Moment;
    commentDateUtc?: moment.Moment;
    replyContent?: EditorContent;
    content: Content;
    schemaVersion: string;

    /**
     * UUID generated to check unique instances when loading content from an API. Reason this exists
     * is so that the editor can be force updated when reloading the editor from an API update
     */
    _uuid: string | undefined;

    // Used to represent an "empty" value/editor. Good for initializing an empty editor value
    public static CreateEmpty() {
        return new EditorContent({
            content: null,
            schemaVersion: currentSchemaVersion,
        });
    }

    public static FromApiV1(data: IEditorApiType, addUUid: boolean = true) {
        return new EditorContent(data, addUUid);
    }

    public static FromApix(data: SerializableEditorModel, addUUid: boolean = true) {
        return new EditorContent(data as unknown as IEditorApiType, addUUid);
    }

    /** Convenience method for simple messages. */
    public static FromPlainText(text: string) {
        return new EditorContent({
            content: {
                type: "doc",
                content: [
                    {
                        type: "paragraph",
                        content: [
                            {
                                type: "text",
                                text,
                            },
                        ],
                    },
                ],
            },
            schemaVersion: currentSchemaVersion,
        });
    }
}

/**
 * The default priority level for most parseHTML definitions within extensions
 */
export const defaultHTMLParserPriority = 50;

/**
 * Dictionary where keys represent supported sizes and values represent the size of the element (in em units)
 */
export const supportedFontSizes = {
    small: 0.7,
    medium: 1,
    large: 1.3,
} as const;

export type TiptapExtensionStorageType<T extends Node | Mark | Extension> =
    T extends Node<infer _O, infer S>
        ? S
        : T extends Mark<infer _O, infer S>
          ? S
          : T extends Extension<infer _O, infer S>
            ? S
            : never;

export interface IBTEditorBaseProps<FormValues> {
    /**
     * The input id
     */
    id: KeyOfOrString<FormValues>;
    /**
     * The data test id of the **content** field
     */
    "data-testid": string;
    /**
     * The value of the editor. You can set the value to `EditorContent.CreateEmpty` to clear the editor or initialize
     * it with an empty value. Because replacing editor content is expensive from a performance perspective, after the first render,
     * the only way to modify the editor content is by changing the UUID of the editorValue. That way the component is still
     * controlled, but we avoid unnecessary performance hits when the content changes frequently (like when typing).
     */
    value: EditorContent;
    /**
     * Returns the current editor content when the editor's content has been changed. Note that this
     * event handler is debounced. Pass in formik's setFieldValue here.
     */
    onChange: (key: KeyOfOrString<FormValues>, value: EditorContent) => void;

    /**
     * Event handler that gets called on blur. Pass in formik's setFieldTouched here.
     */
    onBlur: (key: KeyOfOrString<FormValues>, touched: boolean, shouldValidate?: boolean) => void;

    /**
     * Event handler that gets called on focus.
     */
    onFocus?: () => void;

    /**
     * Property that determines if the editor is in "readonly" mode. If readonly is set, the editor
     * controls are hidden, editor content padding is removed, and the content is displayed like
     * plain-text
     */
    readOnly?: boolean;

    /**
     * Property that determines if the editor needs to hide images.
     */
    hideImages?: boolean;

    /**
     * If true, does not render a border around the editor. Useful if the editor is the focus of the
     * modal/page
     */
    borderless?: boolean;

    /**
     * Auto focus when the editor is first loaded. If undefined, it will not focus on load
     * @example
     * autoFocus="end"
     */
    autoFocus?: FocusPosition;

    /**
     * Debouncing configuration overrides. May be useful for testing purposes
     */
    debouncingContext?: IInputDebouncingContext;

    /**
     * The size of the editor's control bar. Probably shouldn't change this, but it can be used
     * to help testing as the default "auto" doesn't work for jsdom tests
     *
     * @default "auto"
     */
    editorControlSize?: "auto" | EditorControlBreakpointNames;

    /**
     * Provide styles to the editor container
     */
    style?: React.CSSProperties;
}

export const editorStorageKeys = {
    editorState: "editorState",
    editorStateDispatch: "editorStateDispatch",
};
